import React, { Fragment, useEffect, useState } from "react";
import { Row, Table, Button, Col } from "reactstrap";
import { Link } from "react-router-dom";

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";

import { rankItem } from "@tanstack/match-sorter-utils";
import JobListGlobalFilter from "./GlobalSearchFilter";

// Column Filter
const Filter = ({ column }) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <>
      <DebouncedInput
        type="text"
        value={columnFilterValue ?? ""}
        onChange={(value) => column.setFilterValue(value)}
        placeholder="Search..."
        className="w-36 border shadow rounded"
        list={column.id + "list"}
      />
      <div className="h-1" />
    </>
  );
};

// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <React.Fragment>
      <Col sm={4}>
        <input {...props} value={value} onChange={(e) => setValue(e.target.value)} />
      </Col>
    </React.Fragment>
  );
};

const TableContainer = ({
  columns,
  data,
  tableClass,
  theadClass,
  divClassName,
  isBordered,
  isPagination,
  isGlobalFilter,
  paginationWrapper,
  SearchPlaceholder,
  pagination,
  buttonClass,
  buttonName,
  isAddButton,
  isCustomPageSize,
  handleUserClick,
  isJobListGlobalFilter,
  paginationData,
  onChangePagination,
  manualPagination = false,
  pageCount,
  totalRows,
  onRowClick,
  pageSizeAtBottom = false,
}) => {
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [paginationState, setPaginationState] = useState({
    pageIndex: paginationData?.page || 0,
    pageSize: paginationData?.pageSize || 10,
  });

  // In server-driven (manual) mode the parent owns the current page/size, so
  // keep our internal pagination state in sync with those props.
  useEffect(() => {
    if (manualPagination) {
      setPaginationState({
        pageIndex: paginationData?.page || 0,
        pageSize: paginationData?.pageSize || 10,
      });
    }
  }, [manualPagination, paginationData?.page, paginationData?.pageSize]);

  const fuzzyFilter = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank,
    });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns,
    data,
    // With manualPagination, `data` is already one server page; page count
    // comes from the server total.
    manualPagination,
    pageCount: manualPagination ? (pageCount ?? -1) : undefined,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
      pagination: paginationState,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: setPaginationState,
  });

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    setPageIndex,
    nextPage,
    previousPage,
    getState,
  } = table;

  const paginate = (pageNumber, action) => {
    // Server-driven pages refetch via onChangePagination; client-side pages
    // just move TanStack's own pagination state below.
    if (action === 'next') {
      nextPage();
      if (onChangePagination) onChangePagination({ limit: paginationState.pageSize, page: pageNumber });
    } else if (action === 'previous') {
      previousPage();
      if (onChangePagination) onChangePagination({ limit: paginationState.pageSize, page: pageNumber });
    } else if (action === 'first') {
      setPageIndex(0);
      if (onChangePagination) onChangePagination({ limit: paginationState.pageSize, page: 0 });
    } else if (action === 'last') {
      const lastPage = getPageOptions().length - 1;
      setPageIndex(lastPage);
      if (onChangePagination) onChangePagination({ limit: paginationState.pageSize, page: lastPage });
    } else {
      setPageIndex(pageNumber);
      if (onChangePagination) onChangePagination({ limit: paginationState.pageSize, page: pageNumber });
    }
  };

  // Limit page options to 5 visible pages
  const currentPage = getState().pagination.pageIndex;
  const totalPages = getPageOptions().length;
  const visiblePageCount = 5;
  const halfVisible = Math.floor(visiblePageCount / 2);

  let startPage = Math.max(0, currentPage - halfVisible);
  let endPage = Math.min(totalPages - 1, currentPage + halfVisible);

  if (endPage - startPage + 1 < visiblePageCount) {
    if (startPage === 0) {
      endPage = Math.min(totalPages - 1, startPage + visiblePageCount - 1);
    } else if (endPage === totalPages - 1) {
      startPage = Math.max(0, endPage - visiblePageCount + 1);
    }
  }

  const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

  const pageSizeSelect = (
    <select
      className="form-select pageSize mb-2"
      value={table.getState().pagination.pageSize}
      onChange={(e) => {
        const size = Number(e.target.value);
        table.setPageSize(size);
        // Server-paginated: re-fetch with the new size from page 0.
        if (manualPagination && onChangePagination) {
          onChangePagination({ limit: size, page: 0 });
        }
      }}
    >
      {[10, 20, 30, 40, 50, 100].map((pageSize) => (
        <option key={pageSize} value={pageSize}>
          Show {pageSize}
        </option>
      ))}
    </select>
  );

  return (
    <Fragment>
      <Row className="mb-2">
        {isCustomPageSize && !pageSizeAtBottom && (
          <Col sm={2}>{pageSizeSelect}</Col>
        )}

        {isGlobalFilter && (
          <DebouncedInput
            value={globalFilter ?? ""}
            onChange={(value) => setGlobalFilter(String(value))}
            className="form-control search-box me-2 mb-2 d-inline-block"
            placeholder={SearchPlaceholder}
          />
        )}

        {isJobListGlobalFilter && (
          <JobListGlobalFilter setGlobalFilter={setGlobalFilter} />
        )}

        {isAddButton && (
          <Col sm={6} className="text-end">
            <Button
              color="success"
              style={{ borderRadius: "30px" }}
              type="button"
              className={buttonClass || "btn btn-primary"}
              onClick={handleUserClick}
            >
              <i className="mdi mdi-plus me-1"></i> {buttonName || "Create"}
            </Button>
          </Col>
        )}
      </Row>

      <div className={divClassName ? divClassName : "table-responsive"}>
        <Table hover className={tableClass} bordered={isBordered}>
          <thead className={theadClass}>
            {getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className={`${header.column.columnDef.enableSorting
                      ? "sorting sorting_desc"
                      : ""
                      }`}
                  >
                    {header.isPlaceholder ? null : (
                      <Fragment>
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? "cursor-pointer select-none"
                              : "",
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: "",
                            desc: "",
                          }[header.column.getIsSorted()] ?? null}
                        </div>
                        {header.column.getCanFilter() && (
                          <div>
                            <Filter column={header.column} table={table} />
                          </div>
                        )}
                      </Fragment>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                style={onRowClick ? { cursor: "pointer" } : undefined}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {isPagination && (
        <Row className="align-items-center">
          <Col sm={12} md={5}>
            {pageSizeAtBottom ? (
              <div className="d-flex align-items-center gap-2">
                <div style={{ maxWidth: 120 }}>{pageSizeSelect}</div>
                <span className="dataTables_info">
                  of {(totalRows ?? data.length).toLocaleString()}
                </span>
              </div>
            ) : (
              <div className="dataTables_info">
                Showing {getRowModel().rows.length} of {totalRows ?? data.length} Results
              </div>
            )}
          </Col>
          <Col sm={12} md={7}>
            <div className={paginationWrapper}>
              <ul className={pagination}>
                <li
                  className={`paginate_button page-item ${!getCanPreviousPage() ? "disabled" : ""}`}
                >
                  <Link to="#" className="page-link" onClick={() => paginate(0, 'first')}>
                    First
                  </Link>
                </li>
                <li
                  className={`paginate_button page-item previous ${!getCanPreviousPage() ? "disabled" : ""}`}
                >
                  <Link to="#" className="page-link" onClick={() => paginate(getState().pagination.pageIndex - 1, 'previous')}>
                    <i className="mdi mdi-chevron-left"></i>
                  </Link>
                </li>
                {pageNumbers.map((page) => (
                  <li
                    key={page}
                    className={`paginate_button page-item ${getState().pagination.pageIndex === page ? "active" : ""}`}
                  >
                    <Link
                      to="#"
                      className="page-link"
                      onClick={() => paginate(page)}
                    >
                      {page + 1}
                    </Link>
                  </li>
                ))}
                <li
                  className={`paginate_button page-item next ${!getCanNextPage() ? "disabled" : ""}`}
                >
                  <Link to="#" className="page-link" onClick={() => paginate(getState().pagination.pageIndex + 1, 'next')}>
                    <i className="mdi mdi-chevron-right"></i>
                  </Link>
                </li>
                <li
                  className={`paginate_button page-item ${!getCanNextPage() ? "disabled" : ""}`}
                >
                  <Link to="#" className="page-link" onClick={() => paginate(getPageOptions().length - 1, 'last')}>
                    Last
                  </Link>
                </li>
              </ul>
            </div>
          </Col>
        </Row>
      )}
    </Fragment>
  );
};

export default TableContainer;